import { ThemeEvents } from '@theme/events';

/**
 * Surcouche thème des deals Kaching Bundles (quantity breaks).
 *
 * Porte les 3 devs maison de la prod (product.js l.417-671) sur l'archi refonte :
 *  1. Price-sync — le prix du palier sélectionné réécrit le prix principal
 *     (`product-price .price`) et le sticky (`.sticky-add-to-cart__price .price`).
 *  2. Libellés custom des paliers — lit `<bundles-infos data-infos="a/b/c">`
 *     (métafield `custom.formats_bundles`) et insère `.kaching-bundles__bar-format`.
 *  3. OOS paliers — lit `script.kaching-bundles-product[data-main]` (inventaire)
 *     + la config `dealBars` ; grise les paliers infaisables (`is-oos-disabled`)
 *     et bascule sur le plus gros palier dispo si le sélectionné devient OOS.
 *
 * Le DOM Kaching est rendu async par l'app embed et re-rendu à chaque changement
 * de variante : tout est resynchronisé via un MutationObserver rAF-coalescé
 * (remplace les setTimeout 100/200/300/500ms de la prod) + les events app
 * (`variants-changed`, `deal-bar-selected`) + `variant:update` du thème.
 *
 * No-op propre si l'app n'injecte rien (produit sans deal).
 *
 * @extends {HTMLElement}
 */
class AxKachingBundleSync extends HTMLElement {
	/** @type {HTMLElement|null} */
	#block = null;

	/** @type {MutationObserver|null} */
	#waitObserver = null;

	/** @type {MutationObserver|null} */
	#renderObserver = null;

	/** @type {number|null} */
	#renderRaf = null;

	/** @type {AbortController|null} */
	#abortController = null;

	connectedCallback() {
		const block = document.querySelector('kaching-bundle, kaching-bundles-block');
		if (block) {
			this.#setup(block);
			return;
		}

		// L'app embed injecte son bloc après nous — on l'attend.
		this.#waitObserver = new MutationObserver(() => {
			const found = document.querySelector('kaching-bundle, kaching-bundles-block');
			if (!found) return;
			this.#waitObserver?.disconnect();
			this.#waitObserver = null;
			this.#setup(found);
		});
		this.#waitObserver.observe(document.body, { childList: true, subtree: true });
	}

	disconnectedCallback() {
		this.#waitObserver?.disconnect();
		this.#waitObserver = null;
		this.#renderObserver?.disconnect();
		this.#renderObserver = null;
		if (this.#renderRaf) cancelAnimationFrame(this.#renderRaf);
		this.#renderRaf = null;
		this.#abortController?.abort();
		this.#abortController = null;
	}

	/** @param {HTMLElement} block */
	#setup(block) {
		this.#block = block;
		this.#placeAfterVariantPicker(block);
		this.#abortController = new AbortController();
		const { signal } = this.#abortController;

		const refresh = () => this.#scheduleRefresh();

		// Events émis par l'app une fois son DOM à jour.
		block.addEventListener('variants-changed', refresh, { signal });
		block.addEventListener('deal-bar-selected', refresh, { signal });
		block.addEventListener('change', refresh, { signal });

		// Changement de variante côté thème (le morph du bloc price écrase notre sync).
		document.addEventListener(ThemeEvents.variantUpdate, refresh, { signal });

		// Rendu initial + re-renders de l'app (elle rebuild ses barres).
		this.#renderObserver = new MutationObserver(refresh);
		this.#renderObserver.observe(block, { childList: true, subtree: true });

		this.#scheduleRefresh();
	}

	/**
	 * Placement maquette : les paliers suivent immédiatement le sélecteur de
	 * format (variant-picker). L'app embed s'injecte elle-même plus bas dans la
	 * pile de blocks (avant les buy-buttons) sans point d'insertion configurable
	 * — on déplace son élément racine, elle continue de rendre dedans.
	 *
	 * @param {HTMLElement} block
	 */
	#placeAfterVariantPicker(block) {
		const picker = block.parentElement?.querySelector(':scope > variant-picker');
		if (!picker || picker.nextElementSibling === block) return;
		picker.insertAdjacentElement('afterend', block);
	}

	#scheduleRefresh() {
		if (this.#renderRaf) return;
		this.#renderRaf = requestAnimationFrame(() => {
			this.#renderRaf = null;
			this.#applyFormats();
			this.#applyOosState();
			this.#syncPrice();
		});
	}

	// ─── 1. Price-sync ────────────────────────────────────────

	#syncPrice() {
		const selectedBar =
			this.#block?.querySelector('.kaching-bundles__bar--selected') ||
			this.#block
				?.querySelector('.kaching-bundles__bar input[type="radio"]:checked')
				?.closest('.kaching-bundles__bar');
		const priceText = selectedBar
			?.querySelector('.kaching-bundles__bar-price')
			?.textContent.trim();
		if (!priceText) return;

		const targets = document.querySelectorAll(
			'product-price .price__regular:not(.price__hidden) .price, product-price .price__sale:not(.price__hidden) .price-item--sale, .sticky-add-to-cart__price .price__regular:not(.price__hidden) .price, .sticky-add-to-cart__price .price__sale:not(.price__hidden) .price-item--sale',
		);
		for (const el of targets) {
			if (el.textContent !== priceText) el.textContent = priceText;
		}
	}

	// ─── 2. Libellés custom (métafield formats_bundles) ───────

	#applyFormats() {
		const infos = document.querySelector('bundles-infos')?.dataset.infos;
		if (!infos || !this.#block) return;

		const infosArray = infos.split('/');
		const bars = this.#block.querySelectorAll('.kaching-bundles__bar');
		let index = 0;
		for (const bar of bars) {
			const info = infosArray[index];
			index += 1;
			if (!info || bar.querySelector('.kaching-bundles__bar-format')) continue;

			const pricing = bar.querySelector('.kaching-bundles__bar-pricing');
			if (!pricing) continue;

			const format = document.createElement('div');
			format.classList.add('kaching-bundles__bar-format');
			format.textContent = info.trim();
			pricing.insertAdjacentElement('beforebegin', format);
		}
	}

	// ─── 3. OOS paliers ───────────────────────────────────────

	#applyOosState() {
		if (!this.#block) return;

		const productScript = document.querySelector('script.kaching-bundles-product[data-main="true"]');
		if (!productScript) return;

		let productData;
		try {
			productData = JSON.parse(productScript.textContent);
		} catch {
			return;
		}

		const variantInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
		if (!variantInput) return;
		const currentVariantId = Number.parseInt(variantInput.value, 10);

		const variant = productData.variants?.find((v) => v.id === currentVariantId);
		if (!variant) return;

		// La rupture ne bloque que si la variante refuse la survente.
		if (variant.inventoryPolicy !== 'deny') {
			this.#clearOosStates();
			return;
		}

		const inventory = Math.max(0, variant.inventoryQuantity);
		const dealBars = this.#getDealConfig()?.dealBars;
		if (!dealBars) return;

		const bars = this.#block.querySelectorAll('.kaching-bundles__bar');
		let selectedIsOos = false;

		for (const bar of bars) {
			const dealBar = dealBars.find((db) => db.id === bar.getAttribute('data-deal-bar-id'));
			if (!dealBar) continue;

			if (inventory < dealBar.quantity) {
				bar.classList.add('is-oos-disabled');
				if (bar.querySelector('input[type="radio"]')?.checked) selectedIsOos = true;
			} else {
				bar.classList.remove('is-oos-disabled');
			}
		}

		if (selectedIsOos) this.#selectHighestAvailable(bars, dealBars, inventory);
	}

	/** @returns {{ dealBars?: Array<{ id: string, quantity: number }> } | null} */
	#getDealConfig() {
		const productScript = document.querySelector('script.kaching-bundles-product[data-main="true"]');
		const sibling = productScript?.nextElementSibling;
		if (sibling?.tagName === 'SCRIPT') {
			try {
				const data = JSON.parse(sibling.textContent);
				if (data.dealBars) return data;
			} catch {
				// on tente le fallback
			}
		}

		for (const script of document.querySelectorAll('script[type="application/json"]')) {
			try {
				const data = JSON.parse(script.textContent);
				if (data.dealBars) return data;
			} catch {
				// script JSON d'un autre composant
			}
		}
		return null;
	}

	/**
	 * @param {NodeListOf<Element>} bars
	 * @param {Array<{ id: string, quantity: number }>} dealBars
	 * @param {number} inventory
	 */
	#selectHighestAvailable(bars, dealBars, inventory) {
		let bestBar = null;
		let bestQty = 0;

		for (const bar of bars) {
			const dealBar = dealBars.find((db) => db.id === bar.getAttribute('data-deal-bar-id'));
			if (!dealBar) continue;
			if (dealBar.quantity <= inventory && dealBar.quantity > bestQty) {
				bestQty = dealBar.quantity;
				bestBar = bar;
			}
		}

		const radio = bestBar?.querySelector('input[type="radio"]');
		if (radio) {
			radio.checked = true;
			radio.dispatchEvent(new Event('change', { bubbles: true }));
		}
	}

	#clearOosStates() {
		for (const bar of this.#block?.querySelectorAll('.kaching-bundles__bar.is-oos-disabled') ?? []) {
			bar.classList.remove('is-oos-disabled');
		}
	}
}

if (!customElements.get('ax-kaching-bundle-sync')) {
	customElements.define('ax-kaching-bundle-sync', AxKachingBundleSync);
}
